/**
 * /project-invite/:token: the far end of a Teams invitation email
 * (cloud-api's routes/projectInvites.ts, DEPLOYMENT.md §35).
 *
 * Not to be confused with /invite/:token (Claim.tsx), which turns a God View
 * waitlist invite into a NEW account. This one never creates an account: it
 * sits inside the app shell, so a signed-out visitor is sent to sign in first,
 * and the server accepts only when the signed-in email is the invited one.
 * Sign-in has no return path, so a person who had to sign in finds the same
 * invitation waiting on the projects list (PendingInvitations) instead.
 *
 * The invitation is read first, so a wrong account, a used link or an expired
 * one is a plain sentence rather than an Accept button that fails.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { Notice } from '../../components/patterns/Notice';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { Skeleton } from '../../components/patterns/Skeleton';

import { acceptInvitation, declineInvitation, getMyInvitation } from '../../lib/real-api';
import { captureEvent } from '../../lib/analytics/posthog';
import { useAccount } from '../../lib/auth/use-app-data';
import { ApiError } from '../../lib/http';
import { useAsync } from '../../lib/hooks/use-async';
import { t } from '../../lib/i18n/t';

function problemMessage(err: unknown, signedInAs: string): string {
  const code = err instanceof ApiError ? err.code : undefined;
  switch (code) {
    case 'PROJECT_INVITE_WRONG_ACCOUNT':
      return t(
        'This invitation was sent to a different email address. You are signed in as {email}. Sign out and sign in with the address it was sent to.',
        { email: signedInAs },
      );
    case 'PROJECT_INVITE_USED':
      return t('This invitation has already been answered.');
    case 'PROJECT_INVITE_EXPIRED':
      return t('This invitation is no longer valid. Ask for a new one.');
    default:
      return t('This invitation link is not valid.');
  }
}

export default function ProjectInvite() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const account = useAccount();
  const invitation = useAsync(() => getMyInvitation(token), [token]);
  const [busy, setBusy] = useState(false);
  const [answerError, setAnswerError] = useState<string | null>(null);

  useEffect(() => {
    if (invitation.isLoading) return;
    if (invitation.data) {
      captureEvent('project_invite_viewed', { project_slug: invitation.data.projectSlug });
    } else if (invitation.error) {
      const code = invitation.error instanceof ApiError ? invitation.error.code : 'unknown';
      captureEvent('project_invite_invalid', { error_code: code });
    }
  }, [invitation.isLoading, invitation.data, invitation.error]);

  async function answer(accept: boolean): Promise<void> {
    setBusy(true);
    setAnswerError(null);
    const slug = invitation.data?.projectSlug;
    captureEvent(accept ? 'project_invite_accept_clicked' : 'project_invite_decline_clicked', {
      project_slug: slug,
      source: 'email_link',
    });
    try {
      if (accept) {
        const { projectSlug } = await acceptInvitation(token);
        captureEvent('project_invite_accepted', { project_slug: projectSlug, source: 'email_link' });
        navigate(`/p/${encodeURIComponent(projectSlug)}`, { replace: true });
        return;
      }
      await declineInvitation(token);
      captureEvent('project_invite_declined', { project_slug: slug, source: 'email_link' });
      navigate('/projects', { replace: true });
    } catch (err: unknown) {
      const code = err instanceof ApiError ? err.code : 'unknown';
      captureEvent('project_invite_answer_failed', { error_code: code, source: 'email_link' });
      setAnswerError(problemMessage(err, account.email));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <SectionPanel title={t('Invitation')}>
        {invitation.isLoading ? <Skeleton label={t('Checking your invitation')} /> : null}

        {invitation.error ? (
          <>
            <p className="set__say">{problemMessage(invitation.error, account.email)}</p>
            <p>
              <Link className="link-inline" to="/projects">
                {t('Go to your projects')}
              </Link>
            </p>
          </>
        ) : null}

        {invitation.data ? (
          <>
            <p>
              {invitation.data.invitedBy
                ? t('{person} invited you to work on {project}.', {
                    person: invitation.data.invitedBy,
                    project: invitation.data.projectName,
                  })
                : t('You were invited to work on {project}.', {
                    project: invitation.data.projectName,
                  })}
            </p>
            <p className="set__say">
              {t(
                'You will be able to do everything its owner can, from this dashboard, the svcloud CLI, or your own coding agent. Only the owner can delete it.',
              )}
            </p>
            {answerError ? <Notice tone="attention">{answerError}</Notice> : null}
            <div className="set__actions">
              <Button variant="solid" loading={busy} onClick={() => answer(true)}>
                {t('Accept')}
              </Button>
              <Button variant="outline" disabled={busy} onClick={() => answer(false)}>
                {t('Decline')}
              </Button>
            </div>
          </>
        ) : null}
      </SectionPanel>
    </div>
  );
}
